import { Route, Routes, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { AdminAuditPage } from '../features/admin/AdminAuditPage';
import { AdminFeaturesPage } from '../features/admin/AdminFeaturesPage';
import { AdminCharactersPage } from '../features/admin/AdminCharactersPage';
import { CharacterDrawer } from '../features/character/CharacterDrawer';
import { CharacterPage } from '../features/character/CharacterPage';
import { AdminLayout } from '../features/admin/AdminLayout';
import { AdminPaymentsPage } from '../features/admin/AdminPaymentsPage';
import { AdminOverviewPage } from '../features/admin/AdminOverviewPage';
import { AdminDmcaPage } from '../features/admin/AdminDmcaPage';
import { AdminReportsPage } from '../features/admin/AdminReportsPage';
import { AdminAnnouncementsPage } from '../features/admin/AdminAnnouncementsPage';
import { AdminSafetyPage } from '../features/admin/AdminSafetyPage';
import { AdminUsersPage } from '../features/admin/AdminUsersPage';
import { AlbumPage } from '../features/albums/AlbumPage';
import { PlaylistPage } from '../features/library/PlaylistPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { VerifyEmailPage } from '../features/auth/VerifyEmailPage';
import { DiscoverPage } from '../features/discover/DiscoverPage';
import { FavoritesPage } from '../features/favorites/FavoritesPage';
import { FeedPage } from '../features/feed/FeedPage';
import { MessagesPage } from '../features/messages/MessagesPage';
import { HomePage } from '../features/home/HomePage';
import { FollowListPage } from '../features/profile/FollowListPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { ClaimsPage } from '../features/moderation/ClaimsPage';
import { DmcaPage } from '../features/moderation/DmcaPage';
import { PrivacyPage } from '../features/legal/PrivacyPage';
import { WhatsNewPage } from '../features/announcements/WhatsNewPage';
import { TermsPage } from '../features/legal/TermsPage';
import { SearchPage } from '../features/search/SearchPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { StatsPage } from '../features/stats/StatsPage';
import { TrackPage } from '../features/tracks/TrackPage';
import { UploadPage } from '../features/upload/UploadPage';
import { StatusPage } from '../features/home/StatusPage';
import { AdminHostApplicationsPage } from '../features/admin/AdminHostApplicationsPage';
import { AdminUserPage } from '../features/admin/AdminUserPage';
import { EarningsLayout } from '../features/payments/EarningsLayout';
import {
  EarningsBalances,
  EarningsPayouts,
  EarningsSummary,
} from '../features/payments/EarningsPages';
import { QueuePage } from '../features/rooms/QueuePage';
import { HostApplicationPage } from '../features/hosting/HostApplicationPage';
import { ConsolePage } from '../features/rooms/ConsolePage';
import { AlbumDrawer } from '../features/albums/AlbumDrawer';
import { ArtistDrawer } from '../features/profile/ArtistDrawer';
import { SettingsDrawer } from '../features/settings/SettingsDrawer';
import { TrackDrawer } from '../features/tracks/TrackDrawer';
import { AppShell } from './AppShell';
import { RequireAnonymous, RequireAuth } from './RequireAuth';
import { RequirePermission } from './RequirePermission';

/**
 * The route table.
 *
 * Everything nests under `AppShell`, which is what keeps the dock bar mounted
 * across navigation - see the note there.
 *
 * ## The same URL is a page or a drawer, depending on how you got there
 *
 * A `DrawerLink` pushes `/tracks/123` and stashes the page you were on in
 * `location.state.background`. When that is present the table below is rendered
 * against the *background* - so the feed you had scrolled stays exactly where
 * it was - and a second, tiny table renders the real URL as a drawer over it.
 *
 * With no background, which is every cold load, shared link, refresh and typed
 * address, only the first table runs and `/tracks/123` is the page it always
 * was. That is the whole trick: nothing about the URL changes meaning, only
 * what is rendered around it.
 */
export function AppRoutes() {
  const location = useLocation();
  const background = (location.state as { background?: Location } | null)?.background;

  return (
    <>
    <Routes location={background ?? location}>
      <Route element={<AppShell />}>
        <Route index element={<HomePage />} />
        <Route path="status" element={<StatusPage />} />

        {/* The charts (§5). Public, and a read of an hourly snapshot rather
            than a live aggregate - see ShowChartAction. */}
        <Route path="discover" element={<DiscoverPage />} />

        {/* Public: finding music is the point, and a search behind a sign-in
            wall is a wall in front of the reason to sign up. */}
        <Route path="search" element={<SearchPage />} />
        <Route path="tracks/:id" element={<TrackPage />} />

        {/* Public: the action decides what a given viewer may see, so an
            unlisted album is reachable by link and a private one 404s. */}
        <Route path="albums/:id" element={<AlbumPage />} />
        {/*
          Playlists have their own page, though they share a table with albums.

          Same record, different thing: an album is a release with a cover and
          an artist, a playlist is somebody's list of other people's records.
          `AlbumPage` gave the second an empty artwork slot and an artist
          heading over eleven different artists.
        */}
        <Route path="playlists/:id" element={<PlaylistPage />} />

        {/*
          Filing a copyright claim (§11). Public, and it has to be: a
          rightsholder is usually a label or an agent who has never heard of
          this site, and a form behind a sign-up is a form that does not get
          used - see DmcaPage.
        */}
        <Route path="dmca" element={<DmcaPage />} />

        {/* Public and unauthenticated: somebody deciding whether to sign up
            has to be able to read what they would be agreeing to. */}
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        {/* Public: a changelog is an argument for the platform rather than a
            members' area, and the unread dot is the only part that needs a
            session. */}
        <Route path="whats-new" element={<WhatsNewPage />} />

        {/* Signed out only: landing on /login while signed in is a dead end. */}
        <Route element={<RequireAnonymous />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/*
          Reachable signed in or out, deliberately. Both links arrive from an
          email, and mail clients open them in whatever browser they like -
          routinely not the one holding the session. Gating either would strand
          exactly the people who did what they were asked.
        */}
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route path="settings" element={<SettingsPage />} />

          {/* The maker's full-page self. It is reached from your own profile,
              where it opens as a drawer - but a bookmark, a reload or a pasted
              link arrives here with no background location, and without this
              `character` would fall through to `:username`. */}
          <Route path="character" element={<CharacterPage />} />

          {/* Your own numbers. Signed in only, because they are nobody
              else's - see StatsAction on why the route lives under `/me`. */}
          <Route path="stats" element={<StatsPage />} />

          {/* Claims against your own tracks, and how to answer one. */}
          <Route path="claims" element={<ClaimsPage />} />
          {/* Yours alone - the endpoint behind it is /me/favorites. */}
          <Route path="favorites" element={<FavoritesPage />} />
          {/* The follow feed (§7). Signed in only: there is no feed without
              a set of follows to assemble it from. */}
          <Route path="following" element={<FeedPage />} />

          {/* One component, two routes: the inbox, and the inbox with a thread
              open. On a phone the list hides once one is chosen. */}
          <Route path="messages" element={<MessagesPage />} />

          {/* Applying to host (§6.8). Signed in, but not verification-gated:
              the page explains the requirement and the form's own submit is
              where the confirmed address is enforced. */}
          <Route path="host" element={<HostApplicationPage />} />

          {/* Getting paid, one route per surface. Stripe was given these
              addresses one row per page before it would let the platform
              take accounts live - see `EarningsLayout`. */}
          <Route path="earnings" element={<EarningsLayout />}>
            <Route index element={<EarningsSummary />} />
            <Route path="payouts" element={<EarningsPayouts />} />
            <Route path="balances" element={<EarningsBalances />} />
          </Route>

          {/* The DJ console. No id: one person has one station, so there is
              nothing to choose between - and a broadcast is reached through its
              host's profile rather than having a URL of its own. */}
          <Route path="console" element={<ConsolePage />} />

          <Route path="messages/:id" element={<MessagesPage />} />
        </Route>

        {/*
          Verified only, matching the server.
 
          `POST /tracks/init` requires a verified account (§7.1), so an
          unverified one reaching this form would fill it in, pick a file, and
          be refused at the first request. The guard turns that into a clear
          "verify your email first" before any of that work.
        */}
        <Route element={<RequireAuth verified />}>
          <Route path="upload" element={<UploadPage />} />
        </Route>

        {/*
          The admin panel is part of this SPA, behind a permission check, rather
          than a separate build. One auth flow, one deploy - and moderators
          frequently need to see a profile exactly as users see it, which a
          separate app makes awkward.

          These guards decide what to render, never what is allowed: every
          route behind them is enforced independently on the server (§6.7).
        */}
        <Route path="admin" element={<RequirePermission permission="admin.access" />}>
          {/*
            `AdminLayout` owns the width and the navigation for everything
            below, so a new admin page gets both by existing rather than by
            remembering to render them.
          */}
          <Route element={<AdminLayout />}>
          <Route index element={<AdminOverviewPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="features" element={<AdminFeaturesPage />} />
          {/* The character rig, every part on one screen. Nothing here writes
              anything - it is a workbench for the people extending it. */}
          <Route path="characters" element={<AdminCharactersPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          {/* One account, in full. Same permission as the roster - the sections
              that need more are gated inside the response. */}
          <Route path="users/:id" element={<AdminUserPage />} />
          {/* `dmca.view` reads the queue; acting needs `dmca.action`, which
              moderators deliberately do not have - see AdminDmcaPage. */}
          <Route element={<RequirePermission permission="dmca.view" />}>
            <Route path="dmca" element={<AdminDmcaPage />} />
          </Route>

          <Route element={<RequirePermission permission="report.view" />}>
            <Route path="reports" element={<AdminReportsPage />} />
          </Route>
          {/* Its own permission, not `report.view`: reading a machine's guesses
              in bulk means reading things that turned out to be nothing, and
              this list is also who gets the critical alert emails. */}
          <Route element={<RequirePermission permission="safety.view" />}>
            <Route path="safety" element={<AdminSafetyPage />} />
          </Route>
          {/* One permission for reading and writing: the list is what everybody
              can already see at /whats-new plus drafts, and there is no halfway
              trust between "may read our drafts" and "may publish". */}
          <Route element={<RequirePermission permission="announcement.post" />}>
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
          </Route>
          <Route element={<RequirePermission permission="host.review" />}>
            <Route path="host-applications" element={<AdminHostApplicationsPage />} />
          </Route>
          <Route element={<RequirePermission permission="audit.view" />}>
            <Route path="audit" element={<AdminAuditPage />} />
          </Route>
          </Route>
        </Route>

        {/*
          Profiles at the bare username, which is the shape people expect and
          the one that reads well when shared.

          Safe because two things hold together: React Router matches static
          segments before dynamic ones, so every route above wins; and the
          server refuses to register those names at all
          (RegisterAction::RESERVED_USERNAMES). Routing alone would let somebody
          claim `settings` and shadow nothing until that page shipped.

          Last, so it cannot shadow anything - and before the catch-all, so an
          unknown username still renders a "no such profile" page rather than a
          generic 404.
        */}
        <Route path=":username" element={<ProfilePage />} />
        {/* The running order, at an address a host can paste into a stream.
            Public and unauthenticated for the same reason listening is - see
            `QueuePage`. */}
        <Route path=":username/queue" element={<QueuePage />} />
        <Route path=":username/followers" element={<FollowListPage />} />
        <Route path=":username/following" element={<FollowListPage />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>

    {/*
      Only ever mounted over a background, so these two paths appear twice in
      this file without ever both matching. Outside `AppShell` on purpose: the
      drawer is fixed-position chrome, and nesting it under the shell would put
      it inside the `<main>` whose scroll it locks.
    */}
    {background && (
      <Routes>
        <Route path="tracks/:id" element={<TrackDrawer />} />
        <Route path="albums/:id" element={<AlbumDrawer />} />
        {/*
          Before `:username`, which is a single segment and would otherwise
          swallow it. Reached only from the "Edit profile" button on your own
          profile - see `SettingsDrawer`.
        */}
        <Route path="settings" element={<SettingsDrawer />} />
        {/*
          Opened from your own character on your own profile, which is also
          where it will be standing - see `CharacterDrawer`. Before
          `:username` for the same reason `settings` is.
        */}
        <Route path="character" element={<CharacterDrawer />} />
        {/*
          Last, because `:username` is a single segment and would otherwise
          shadow the two above. Safe as a catch-all here in a way it is not in
          the main table: this block only renders when something deliberately
          pushed a background, which nothing but `DrawerLink` does.
        */}
        <Route path=":username" element={<ArtistDrawer />} />
      </Routes>
    )}
    </>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-24 text-center">
      <h1 className="text-2xl font-semibold">Nothing here</h1>
      <p className="mt-2 text-soft">That page does not exist.</p>
    </div>
  );
}
